/**
 * Last-known-value cache for read-only ledger/market data.
 *
 * Purpose is purely presentational: the home screen should paint the balance it
 * last saw instantly instead of flashing "0" while the first XRPL request is in
 * flight. Nothing secret is ever written here — only public account data that
 * anyone can read from the ledger for this address.
 */

const PREFIX = "vaultkey.cache.v1:";

function available(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function readCache<T>(key: string): T | undefined {
  if (!available()) return undefined;
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return undefined;
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

export function writeCache<T>(key: string, value: T): T {
  if (!available()) return value;
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    /* storage full or blocked — cache is best-effort */
  }
  return value;
}

/** Wraps a fetcher so every successful result is remembered for instant paint. */
export function cached<T>(key: string, fetcher: () => Promise<T>): () => Promise<T> {
  return async () => writeCache(key, await fetcher());
}
